import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  MapPin, CheckCircle, ArrowRight, Phone, MessageCircle,
  Calendar, TrendingUp, Shield, Zap, Star, ChevronLeft
} from "lucide-react";
import { PROPERTY_DETAIL, FORMS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = "917977009190";

const ScoreBar = ({ label, value, max = 10 }) => (
  <div className="mb-4">
    <div className="flex justify-between mb-1">
      <span className="text-xs text-[#A0A0A0]">{label}</span>
      <span className="text-xs text-[#D4AF37] font-display">{value}/10</span>
    </div>
    <div className="h-[2px] bg-white/5 rounded-full">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${(value / max) * 100}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F4D98B] rounded-full"
      />
    </div>
  </div>
);

const PropertyDetail = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [formStatus, setFormStatus] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/properties/${id}`)
      .then((r) => setProperty(r.data))
      .catch(() => setProperty(null))
      .finally(() => setLoading(false));
  }, [id]);

  const handleEnquiry = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        lead_type: "property_enquiry",
        property_id: id,
        property_name: property?.title,
      });
      setFormStatus("success");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      setFormStatus("error");
    }
    setFormLoading(false);
  };

  if (loading) return (
    <div className="bg-[#080808] min-h-screen flex items-center justify-center pt-24">
      <div className="w-8 h-8 border border-[#D4AF37]/20 border-t-[#D4AF37] rounded-full animate-spin" />
    </div>
  );

  if (!property) return (
    <div className="bg-[#080808] min-h-screen flex flex-col items-center justify-center pt-24">
      <p className="text-[#666] mb-4">Property not found.</p>
      <Link to="/properties" className="btn-gold text-xs py-3 px-6">Back to Properties</Link>
    </div>
  );

  const whatsappMsg = encodeURIComponent(`Hi, I'm interested in ${property.title} at ${property.location}. Price: ${property.price_display}. Please share more details.`);

  return (
    <div className="bg-[#080808] min-h-screen pt-24">
      {/* Hero image */}
      <div className="relative h-[50vh] overflow-hidden">
        <img src={property.image} alt={property.title}
          className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/40 to-transparent" />
        <div className="absolute bottom-8 left-0 right-0">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Link to="/properties" className="flex items-center gap-2 text-xs text-[#D4AF37] mb-4 hover:text-[#FAF8F3] transition-colors">
              <ChevronLeft size={14} /> Back to Properties
            </Link>
            <div className="flex flex-wrap gap-2 mb-3">
              {(property.tags || []).map((tag) => (
                <span key={tag} className="text-[9px] tracking-wider uppercase px-2 py-1 bg-black/70 text-[#D4AF37] border border-[#D4AF37]/20">{tag}</span>
              ))}
            </div>
            <h1 data-testid={PROPERTY_DETAIL.title}
              className="font-display text-3xl md:text-4xl text-[#FAF8F3] font-light mb-2">
              {property.title}
            </h1>
            <div className="flex items-center gap-2 text-sm text-[#A0A0A0]">
              <MapPin size={12} className="text-[#D4AF37]" />
              <span>{property.location}</span>
              <span className="text-[#444]">·</span>
              <span>{property.developer}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-10">
            {/* Key metrics */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              {[
                { label: "Price Range", value: property.price_display },
                { label: "Status", value: property.status },
                { label: "Possession", value: property.possession },
                { label: "Carpet Area", value: property.carpet_area },
              ].map((m) => (
                <div key={m.label} className="glass-gold p-4">
                  <p className="text-[#D4AF37] text-sm font-display font-light">{m.value}</p>
                  <p className="text-[#444] text-[10px] tracking-wider mt-1">{m.label}</p>
                </div>
              ))}
            </motion.div>

            {/* Description */}
            <div>
              <p className="section-label mb-3">About This Property</p>
              <p className="text-[#A0A0A0] text-sm leading-relaxed">{property.description}</p>
            </div>

            {/* Configurations */}
            <div>
              <p className="section-label mb-3">Configurations</p>
              <div className="flex flex-wrap gap-3">
                {(property.configurations || []).map((c) => (
                  <div key={c} className="border border-[#D4AF37]/20 px-4 py-2 text-sm text-[#D4AF37]">{c}</div>
                ))}
              </div>
            </div>

            {/* Highlights */}
            <div>
              <p className="section-label mb-3">Infrastructure Highlights</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(property.highlights || []).map((h) => (
                  <div key={h} className="flex items-center gap-3 border border-white/5 p-3">
                    <Zap size={12} className="text-[#D4AF37] shrink-0" />
                    <span className="text-xs text-[#A0A0A0]">{h}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities */}
            <div>
              <p className="section-label mb-3">Premium Amenities</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {(property.amenities || []).map((a) => (
                  <div key={a} className="flex items-center gap-2">
                    <CheckCircle size={12} className="text-[#D4AF37] shrink-0" />
                    <span className="text-xs text-[#A0A0A0]">{a}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Investment scores */}
            <div>
              <p className="section-label mb-4">Investment Intelligence Scores</p>
              <div className="glass-gold p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  <ScoreBar label="ROI Score" value={property.roi_score} />
                  <ScoreBar label="Infrastructure Score" value={property.infrastructure_score} />
                  <ScoreBar label="Growth Score" value={property.growth_score} />
                  <ScoreBar label="Rental Score" value={property.rental_score} />
                  <ScoreBar label="Connectivity Score" value={property.connectivity_score} />
                </div>
                <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[#D4AF37] text-base font-display">{property.appreciation_potential}</p>
                    <p className="text-[#444] text-[10px] tracking-wider mt-1">Appreciation Potential</p>
                  </div>
                  <div>
                    <p className="text-[#D4AF37] text-base font-display">{property.annual_rental_yield}</p>
                    <p className="text-[#444] text-[10px] tracking-wider mt-1">Annual Rental Yield</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price card */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
              className="glass-gold p-6 sticky top-28"
            >
              <p className="font-display text-2xl text-[#D4AF37] mb-1">{property.price_display}</p>
              <p className="text-xs text-[#666] mb-6">{property.developer} · {property.location}</p>

              {/* Quick actions */}
              <div className="space-y-3 mb-6">
                <a href={`https://wa.me/${WHATSAPP}?text=${whatsappMsg}`} target="_blank" rel="noopener noreferrer"
                  data-testid={PROPERTY_DETAIL.whatsappCta}
                  className="btn-gold-solid btn-gold w-full justify-center text-xs py-3"
                >
                  <MessageCircle size={13} className="mr-2" /> WhatsApp Enquiry
                </a>
                <a href={`tel:+917977009190`}
                  className="btn-gold w-full justify-center text-xs py-3"
                >
                  <Phone size={13} className="mr-2" /> Call Now
                </a>
              </div>

              {/* Enquiry form */}
              <div>
                <p className="text-xs text-[#D4AF37] tracking-wider uppercase mb-4">Request Details</p>
                {formStatus === "success" ? (
                  <div data-testid={FORMS.successMsg} className="text-center py-4">
                    <CheckCircle size={24} className="text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-sm text-[#FAF8F3]">Request sent!</p>
                    <p className="text-xs text-[#666] mt-1">We'll contact you shortly.</p>
                  </div>
                ) : (
                  <form data-testid={PROPERTY_DETAIL.enquiryForm} onSubmit={handleEnquiry} className="space-y-4">
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="input-luxury text-sm" placeholder="Your Name *" required />
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="input-luxury text-sm" placeholder="Phone Number *" required />
                    <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="input-luxury text-sm" type="email" placeholder="Email (optional)" />
                    <button data-testid={PROPERTY_DETAIL.enquirySubmit} type="submit" disabled={formLoading}
                      className="btn-gold w-full justify-center text-xs py-3">
                      {formLoading ? "Sending..." : "Request Details"}
                      {!formLoading && <ArrowRight size={12} className="inline ml-1" />}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
